import axios from 'axios';
import { toast } from 'react-toastify';

import { UsersApi } from '@/api/clients/users/users.api';
import { API_BASE_URL } from '@/config';
import { buildFacilityUserScopePayload } from '../components/facilityUserScope.utils';

const buildCreatePayload = ({ permissionsType, ...baseUserData }) => {
  const createPayload = {
    email: baseUserData.email,
    name: baseUserData.name,
    mfa_type: baseUserData.mfaType,
    language_preference: baseUserData.languagePreference,
    organization_id: baseUserData.organizationId,
    facility_ids: permissionsType === 'role' ? [] : baseUserData.facilityIds,
    has_all_facilities_access:
      permissionsType === 'role' ? false : baseUserData.hasAllFacilitiesAccess,
    permissions_type: permissionsType,
  };

  if (permissionsType === 'role') {
    createPayload.role_id = baseUserData.roleId;
    return createPayload;
  }

  const facilityPermissions = [];

  baseUserData.permissions?.forEach((permission) => {
    if (permission.organizationId) {
      createPayload.organization_permissions = permission.permissions;
      return;
    }

    facilityPermissions.push({
      facilityId: permission.facilityId,
      permissions: permission.permissions,
    });
  });

  if (facilityPermissions.length > 0) {
    createPayload.facility_permissions = facilityPermissions;
  }

  return createPayload;
};

export const useFacilityUserActions = ({
  dataTableRef,
  navigate,
  requestConfirmation,
  setFormDialogOpen,
  setLoading,
  showSuccessMessage,
  showErrorMessage,
}) => {
  const confirmDeleteUser = async (userId) => {
    try {
      setLoading(true);
      await axios.delete(API_BASE_URL + '/delete_facility_user/' + userId);

      dataTableRef.current?.refresh();
      showSuccessMessage('User deleted successfully');
    } catch (err) {
      console.error('Error deleting user:', err);
      showErrorMessage('Failed to delete user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const confirmToggleUser = async (userId, disableUser) => {
    try {
      setLoading(true);
      await axios.put(API_BASE_URL + '/toggle_facility_user', {
        disableUser,
        id: userId,
      });

      dataTableRef.current?.refresh();

      const action = disableUser ? 'disabled' : 'enabled';
      showSuccessMessage(`User ${action} successfully`);
    } catch (err) {
      console.error('Error toggling user status:', err);
      const action = disableUser ? 'disabling' : 'enabling';
      showErrorMessage(`Failed to ${action} user. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setFormDialogOpen(true);
  };

  const handleEditUser = (userId) => {
    navigate(`/admin/usermanagement/${userId}`);
  };

  const handleDeleteUser = (userId) => {
    requestConfirmation('Are you sure you want to delete this user?', () =>
      confirmDeleteUser(userId),
    );
  };

  const handleToggleUser = (userId, disableUser) => {
    const action = disableUser ? 'disable' : 'enable';
    requestConfirmation(`Are you sure you want to ${action} this user?`, () =>
      confirmToggleUser(userId, disableUser),
    );
  };

  const handleSaveUser = async (userData) => {
    try {
      setLoading(true);

      const {
        accessMode,
        facilityGroupIds,
        permissionsType,
        ...baseUserData
      } = userData;
      const createPayload = buildCreatePayload({
        ...baseUserData,
        permissionsType,
      });
      const response = await UsersApi.createFacilityUser(createPayload);
      const newUser = response.data?.data || response.data;
      const newUserId = newUser?.userId ?? newUser?.id;

      if (newUserId && permissionsType === 'role') {
        try {
          await UsersApi.updateFacilityUserScope(
            newUserId,
            buildFacilityUserScopePayload({
              accessMode,
              facilityIds: baseUserData.facilityIds,
              facilityGroupIds,
            }),
          );
        } catch (scopeError) {
          console.error('Error saving facility user scope:', scopeError);
          toast.warn(
            'User was created, but saving facility access failed. Review the user scope and save again.',
          );
        }
      }

      setFormDialogOpen(false);

      if (newUserId) {
        navigate(`/admin/usermanagement/${newUserId}`);
        return;
      }

      showSuccessMessage('User created successfully');
    } catch (err) {
      console.error('Error saving user:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    handleCreateUser,
    handleEditUser,
    handleDeleteUser,
    handleToggleUser,
    handleSaveUser,
  };
};
